/**
 * PAY-01D Phase E: Gateway Payments Workspace
 * Route: /ar/gateway-payments  (payment.gateway.view)
 *
 * Tabs:
 *   Overview    — KPI cards + recent orders table
 *   Initiate    — Create QR / Payment Link against installment or invoice
 *   History     — Full transaction log with status filters
 *   Settlements — Settlement import + reconciliation view
 *   Config      — Gateway configuration (read-only; activation blocked)
 *
 * UAT Invariants:
 *   - All orders created via MockPaymentGateway (is_mock=true shown as badge)
 *   - No real customer notifications
 *   - No live credentials visible in UI
 *   - Webhook simulation via test panel
 */
import { useState, useEffect, useCallback, useRef } from "react";
import { toast } from "sonner";
import {
  QrCode, Link2, RefreshCw, ChevronDown, ChevronUp,
  CheckCircle2, XCircle, Clock, AlertTriangle, Settings2,
  ArrowDownLeft, Wallet, BarChart2, Shield, Eye, Ban,
  Copy, ExternalLink, Download, FileText, CalendarRange,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { gatewayApi, arPayPlanApi, formatError } from "@/services/api";

// ── Helpers ───────────────────────────────────────────────────────────────────
const fmt = (minor, currency = "INR") => {
  if (minor == null) return "—";
  return new Intl.NumberFormat("en-IN", { style: "currency", currency, minimumFractionDigits: 0 }).format(minor / 100);
};
const fmtDate = (iso) => {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
};
const fmtShort = (iso) => {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
};

const ORDER_STATUS = {
  CREATED:          { label: "Created",          color: "#64748B", bg: "#F1F5F9" },
  ACTIVE:           { label: "Active",            color: "#2563EB", bg: "#EFF6FF" },
  PAYMENT_RECEIVED: { label: "Payment Received",  color: "#166534", bg: "#F0FDF4" },
  PAYMENT_FAILED:   { label: "Payment Failed",    color: "#DC2626", bg: "#FEF2F2" },
  EXPIRED:          { label: "Expired",           color: "#92400E", bg: "#FFFBEB" },
  CANCELLED:        { label: "Cancelled",         color: "#6B7280", bg: "#F9FAFB" },
  REFUND_INITIATED: { label: "Refund Initiated",  color: "#7C3AED", bg: "#F5F3FF" },
};

const TYPE_ICONS = {
  QR_CODE:       QrCode,
  PAYMENT_LINK:  Link2,
  STANDARD:      Wallet,
};

function StatusBadge({ status }) {
  const cfg = ORDER_STATUS[status] || { label: status, color: "#64748B", bg: "#F1F5F9" };
  return (
    <span style={{ background: cfg.bg, color: cfg.color, border: `1px solid ${cfg.color}30`,
      fontSize: "11px", fontWeight: 600, padding: "2px 8px", borderRadius: "9999px", whiteSpace: "nowrap" }}>
      {cfg.label}
    </span>
  );
}

function MockBadge({ isMock }) {
  return isMock ? (
    <span style={{ background: "#FFF3E0", color: "#E65100", fontSize: "10px", fontWeight: 700,
      padding: "1px 6px", borderRadius: "9999px", border: "1px solid #FFB74D80" }}>
      MOCK
    </span>
  ) : (
    <span style={{ background: "#E8F5E9", color: "#1B5E20", fontSize: "10px", fontWeight: 700,
      padding: "1px 6px", borderRadius: "9999px", border: "1px solid #81C78480" }}>
      LIVE
    </span>
  );
}

const TABS = [
  { id: "overview",    label: "Overview",     icon: BarChart2 },
  { id: "initiate",    label: "Initiate",     icon: QrCode },
  { id: "history",     label: "History",      icon: Clock },
  { id: "settlements", label: "Settlements",  icon: ArrowDownLeft },
  { id: "config",      label: "Config",       icon: Settings2 },
];

// ═══════════════════════════════════════════════════════════════════════════════
// OVERVIEW TAB
// ═══════════════════════════════════════════════════════════════════════════════
function OverviewTab({ summary, orders, loading, onRefresh }) {
  const cards = [
    { label: "Total Orders",     value: summary?.total_orders ?? 0,    Icon: Wallet,       color: "#2563EB" },
    { label: "Active",           value: summary?.active ?? 0,           Icon: Clock,        color: "#D97706" },
    { label: "Payment Received", value: summary?.payment_received ?? 0, Icon: CheckCircle2, color: "#166534" },
    { label: "Failed / Expired", value: (summary?.failed ?? 0) + (summary?.expired ?? 0), Icon: XCircle, color: "#DC2626" },
    { label: "Total Collected",  value: fmt(summary?.total_collected_minor ?? 0), Icon: ArrowDownLeft, color: "#7C3AED", isAmount: true },
    { label: "Mock Orders",      value: summary?.mock_orders ?? 0,      Icon: Shield,       color: "#64748B" },
  ];

  return (
    <div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "16px", marginBottom: "24px" }}>
        {cards.map(({ label, value, Icon, color, isAmount }) => (
          <div key={label} style={{ background: "#fff", border: "1px solid #E2E8F0", borderRadius: "12px", padding: "20px" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <span style={{ fontSize: "13px", color: "#64748B", fontWeight: 500 }}>{label}</span>
              <Icon size={18} color={color} />
            </div>
            <div style={{ fontSize: isAmount ? "20px" : "28px", fontWeight: 700, color: "#0F172A", marginTop: "8px" }}>
              {value}
            </div>
          </div>
        ))}
      </div>

      <div style={{ background: "#fff", border: "1px solid #E2E8F0", borderRadius: "12px", padding: "20px" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }}>
          <h3 style={{ fontSize: "15px", fontWeight: 600, color: "#0F172A", margin: 0 }}>Recent Payment Orders</h3>
          <button onClick={onRefresh} style={{ background: "none", border: "none", cursor: "pointer", color: "#64748B" }}>
            <RefreshCw size={16} className={loading ? "animate-spin" : ""} />
          </button>
        </div>
        {orders.length === 0 ? (
          <div style={{ textAlign: "center", padding: "40px", color: "#94A3B8" }}>
            <QrCode size={40} style={{ margin: "0 auto 12px", opacity: 0.4 }} />
            <p style={{ fontSize: "14px" }}>No gateway orders yet. Use the Initiate tab to create a QR or payment link.</p>
          </div>
        ) : (
          <table style={{ width: "100%", borderCollapse: "collapse" }} data-testid="gateway-orders-table">
            <thead>
              <tr style={{ borderBottom: "1px solid #F1F5F9" }}>
                {["Type", "Amount", "Description", "Status", "Mock", "Created"].map(h => (
                  <th key={h} style={{ textAlign: "left", padding: "8px 12px", fontSize: "12px", color: "#64748B", fontWeight: 600 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {orders.slice(0, 10).map(o => {
                const TIcon = TYPE_ICONS[o.order_type] || Wallet;
                return (
                  <tr key={o.id} style={{ borderBottom: "1px solid #F8FAFC" }}>
                    <td style={{ padding: "10px 12px" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                        <TIcon size={14} color="#64748B" />
                        <span style={{ fontSize: "12px", color: "#475569" }}>{o.order_type?.replace("_", " ")}</span>
                      </div>
                    </td>
                    <td style={{ padding: "10px 12px", fontSize: "13px", fontWeight: 600, color: "#0F172A" }}>{fmt(o.amount_minor)}</td>
                    <td style={{ padding: "10px 12px", fontSize: "12px", color: "#64748B", maxWidth: "200px" }}>
                      <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", display: "block" }}>{o.description || "—"}</span>
                    </td>
                    <td style={{ padding: "10px 12px" }}><StatusBadge status={o.status} /></td>
                    <td style={{ padding: "10px 12px" }}><MockBadge isMock={o.is_mock} /></td>
                    <td style={{ padding: "10px 12px", fontSize: "12px", color: "#64748B" }}>{fmtShort(o.created_at)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// INITIATE TAB — QR / PAYMENT LINK CREATION
// ═══════════════════════════════════════════════════════════════════════════════
function InitiateTab({ onCreated }) {
  const [mode, setMode] = useState("qr");
  const [plans, setPlans] = useState([]);
  const [installments, setInstallments] = useState([]);
  const [selectedPlan, setSelectedPlan] = useState("");
  const [selectedInst, setSelectedInst] = useState("");
  const [expiryMins, setExpiryMins] = useState(1440);
  const [expiryDays, setExpiryDays] = useState(7);
  const [description, setDescription] = useState("");
  const [customerEmail, setCustomerEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    arPayPlanApi.list({ status: "ACTIVE", limit: 100 }).then(r => setPlans(r.data?.items || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (!selectedPlan) { setInstallments([]); setSelectedInst(""); return; }
    arPayPlanApi.listInstallments(selectedPlan).then(r => {
      const active = (r.data?.items || []).filter(i => !["PAID", "WAIVED", "CANCELLED"].includes(i.status));
      setInstallments(active);
    }).catch(() => {});
  }, [selectedPlan]);

  const handleCreate = async () => {
    if (!selectedPlan || !selectedInst) { toast.error("Select a plan and installment"); return; }
    setLoading(true); setResult(null);
    try {
      if (mode === "qr") {
        const r = await gatewayApi.createQR({ installment_id: selectedInst, plan_id: selectedPlan, description, expiry_minutes: expiryMins });
        setResult({ type: "qr", ...r.data });
        toast.success("QR code created (MOCK mode)");
      } else {
        const r = await gatewayApi.createPaymentLink({ installment_id: selectedInst, plan_id: selectedPlan, description, customer_email: customerEmail || undefined, expiry_days: expiryDays });
        setResult({ type: "link", ...r.data });
        toast.success("Payment link created (MOCK mode)");
      }
      onCreated();
    } catch (e) {
      toast.error(formatError(e));
    } finally {
      setLoading(false);
    }
  };

  const selectedInstData = installments.find(i => i.id === selectedInst);
  const outstanding = selectedInstData ? (selectedInstData.scheduled_amount_minor - selectedInstData.applied_amount_minor) : 0;

  return (
    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "24px" }}>
      {/* Left: Create form */}
      <div style={{ background: "#fff", border: "1px solid #E2E8F0", borderRadius: "12px", padding: "24px" }}>
        <h3 style={{ fontSize: "15px", fontWeight: 600, color: "#0F172A", margin: "0 0 6px" }}>Create Payment Request</h3>
        <p style={{ fontSize: "12px", color: "#94A3B8", margin: "0 0 20px" }}>
          All requests use MOCK gateway (UAT mode). No real transactions will occur.
        </p>

        {/* Mode toggle */}
        <div style={{ display: "flex", gap: "8px", marginBottom: "20px", background: "#F8FAFC", borderRadius: "8px", padding: "4px" }}>
          {[{ id: "qr", label: "Dynamic QR", Icon: QrCode }, { id: "link", label: "Payment Link", Icon: Link2 }].map(({ id, label, Icon }) => (
            <button key={id} onClick={() => setMode(id)} data-testid={`mode-${id}`}
              style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: "6px",
                padding: "8px", borderRadius: "6px", border: "none", cursor: "pointer", fontWeight: 600, fontSize: "13px",
                background: mode === id ? "#fff" : "transparent",
                color: mode === id ? "#2563EB" : "#64748B",
                boxShadow: mode === id ? "0 1px 3px rgba(0,0,0,0.1)" : "none" }}>
              <Icon size={14} />{label}
            </button>
          ))}
        </div>

        {/* Plan selector */}
        <div style={{ marginBottom: "14px" }}>
          <label style={{ fontSize: "12px", fontWeight: 600, color: "#374151", display: "block", marginBottom: "4px" }}>Payment Plan *</label>
          <select value={selectedPlan} onChange={e => { setSelectedPlan(e.target.value); setSelectedInst(""); }}
            data-testid="plan-select"
            style={{ width: "100%", padding: "8px 10px", border: "1px solid #E2E8F0", borderRadius: "8px", fontSize: "13px", color: "#0F172A" }}>
            <option value="">— Select active plan —</option>
            {plans.map(p => <option key={p.id} value={p.id}>{p.plan_name} ({p.plan_number})</option>)}
          </select>
        </div>

        {/* Installment selector */}
        <div style={{ marginBottom: "14px" }}>
          <label style={{ fontSize: "12px", fontWeight: 600, color: "#374151", display: "block", marginBottom: "4px" }}>Installment *</label>
          <select value={selectedInst} onChange={e => setSelectedInst(e.target.value)}
            disabled={!selectedPlan || installments.length === 0}
            data-testid="installment-select"
            style={{ width: "100%", padding: "8px 10px", border: "1px solid #E2E8F0", borderRadius: "8px", fontSize: "13px", color: "#0F172A" }}>
            <option value="">— Select installment —</option>
            {installments.map(i => (
              <option key={i.id} value={i.id}>
                {i.milestone_label} — {fmt(i.scheduled_amount_minor - i.applied_amount_minor)} outstanding ({i.status})
              </option>
            ))}
          </select>
          {selectedInstData && (
            <p style={{ fontSize: "11px", color: "#64748B", margin: "4px 0 0" }}>
              Due: {fmtShort(selectedInstData.due_date)} · Outstanding: {fmt(outstanding)}
            </p>
          )}
        </div>

        {/* Description */}
        <div style={{ marginBottom: "14px" }}>
          <label style={{ fontSize: "12px", fontWeight: 600, color: "#374151", display: "block", marginBottom: "4px" }}>Description (optional)</label>
          <input value={description} onChange={e => setDescription(e.target.value)} placeholder="Auto-generated if blank"
            style={{ width: "100%", padding: "8px 10px", border: "1px solid #E2E8F0", borderRadius: "8px", fontSize: "13px", boxSizing: "border-box" }} />
        </div>

        {/* Mode-specific fields */}
        {mode === "qr" && (
          <div style={{ marginBottom: "14px" }}>
            <label style={{ fontSize: "12px", fontWeight: 600, color: "#374151", display: "block", marginBottom: "4px" }}>Expiry (minutes)</label>
            <input type="number" value={expiryMins} onChange={e => setExpiryMins(Number(e.target.value))}
              min={5} max={5760}
              style={{ width: "100%", padding: "8px 10px", border: "1px solid #E2E8F0", borderRadius: "8px", fontSize: "13px", boxSizing: "border-box" }} />
            <p style={{ fontSize: "11px", color: "#94A3B8", margin: "4px 0 0" }}>Default 1440 = 24h. Max 5760 = 4 days.</p>
          </div>
        )}
        {mode === "link" && (
          <>
            <div style={{ marginBottom: "14px" }}>
              <label style={{ fontSize: "12px", fontWeight: 600, color: "#374151", display: "block", marginBottom: "4px" }}>Customer Email (optional)</label>
              <input value={customerEmail} onChange={e => setCustomerEmail(e.target.value)} placeholder="No Razorpay notification in UAT"
                style={{ width: "100%", padding: "8px 10px", border: "1px solid #E2E8F0", borderRadius: "8px", fontSize: "13px", boxSizing: "border-box" }} />
            </div>
            <div style={{ marginBottom: "14px" }}>
              <label style={{ fontSize: "12px", fontWeight: 600, color: "#374151", display: "block", marginBottom: "4px" }}>Expiry (days)</label>
              <input type="number" value={expiryDays} onChange={e => setExpiryDays(Number(e.target.value))}
                min={1} max={30}
                style={{ width: "100%", padding: "8px 10px", border: "1px solid #E2E8F0", borderRadius: "8px", fontSize: "13px", boxSizing: "border-box" }} />
            </div>
          </>
        )}

        <button onClick={handleCreate} disabled={loading || !selectedPlan || !selectedInst}
          data-testid="create-order-btn"
          style={{ width: "100%", padding: "10px", background: loading ? "#94A3B8" : "#2563EB",
            color: "#fff", border: "none", borderRadius: "8px", fontWeight: 600, fontSize: "14px",
            cursor: loading ? "not-allowed" : "pointer" }}>
          {loading ? "Creating…" : mode === "qr" ? "Generate QR Code" : "Create Payment Link"}
        </button>
      </div>

      {/* Right: Result panel */}
      <div style={{ background: "#fff", border: "1px solid #E2E8F0", borderRadius: "12px", padding: "24px" }}>
        <h3 style={{ fontSize: "15px", fontWeight: 600, color: "#0F172A", margin: "0 0 16px" }}>Result</h3>
        {!result ? (
          <div style={{ textAlign: "center", padding: "60px 20px", color: "#94A3B8" }}>
            <QrCode size={48} style={{ margin: "0 auto 16px", opacity: 0.3 }} />
            <p style={{ fontSize: "14px" }}>Create a QR or payment link to see it here.</p>
          </div>
        ) : result.type === "qr" ? (
          <QRResultPanel result={result} />
        ) : (
          <LinkResultPanel result={result} />
        )}
      </div>
    </div>
  );
}

function QRResultPanel({ result }) {
  const [copied, setCopied] = useState(false);

  const copyUPI = () => {
    if (result.qr_raw_string) {
      navigator.clipboard.writeText(result.qr_raw_string).then(() => {
        setCopied(true); setTimeout(() => setCopied(false), 2000);
      });
    }
  };

  return (
    <div>
      <div style={{ background: "#F0FDF4", border: "1px solid #BBF7D0", borderRadius: "8px", padding: "12px", marginBottom: "16px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <CheckCircle2 size={16} color="#166534" />
          <span style={{ fontSize: "13px", fontWeight: 600, color: "#166534" }}>QR Code Created</span>
          <MockBadge isMock={result.is_mock} />
        </div>
      </div>

      {/* QR Display */}
      {result.qr_image_url ? (
        <div style={{ textAlign: "center", marginBottom: "16px" }}>
          <img src={result.qr_image_url} alt="QR Code" style={{ maxWidth: "200px", border: "1px solid #E2E8F0", borderRadius: "8px", padding: "8px" }} />
        </div>
      ) : (
        <div data-testid="mock-qr-display" style={{ background: "#F8FAFC", border: "2px dashed #CBD5E1", borderRadius: "8px", padding: "24px", textAlign: "center", marginBottom: "16px" }}>
          <QrCode size={64} color="#94A3B8" style={{ margin: "0 auto 8px" }} />
          <p style={{ fontSize: "12px", color: "#64748B", margin: "0 0 4px" }}>MOCK QR — No image in sandbox mode</p>
          {result.qr_raw_string && (
            <p style={{ fontSize: "10px", color: "#94A3B8", wordBreak: "break-all", margin: 0 }}>
              {result.qr_raw_string.slice(0, 80)}…
            </p>
          )}
        </div>
      )}

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px", marginBottom: "12px" }}>
        <InfoCell label="Amount" value={fmt(result.amount_minor)} />
        <InfoCell label="Order ID" value={result.id?.slice(0, 12) + "…"} />
        <InfoCell label="Expiry" value={fmtShort(result.expiry_at)} />
        <InfoCell label="Status" value={result.status} />
      </div>

      {result.qr_raw_string && (
        <button onClick={copyUPI} style={{ width: "100%", padding: "8px", background: "#F8FAFC", border: "1px solid #E2E8F0",
          borderRadius: "8px", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: "6px",
          fontSize: "13px", color: "#475569" }}>
          <Copy size={14} />{copied ? "Copied!" : "Copy UPI URI"}
        </button>
      )}
    </div>
  );
}

function LinkResultPanel({ result }) {
  const [copied, setCopied] = useState(false);
  const copyURL = () => {
    navigator.clipboard.writeText(result.short_url || "").then(() => {
      setCopied(true); setTimeout(() => setCopied(false), 2000);
    });
  };
  return (
    <div>
      <div style={{ background: "#EFF6FF", border: "1px solid #BFDBFE", borderRadius: "8px", padding: "12px", marginBottom: "16px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <CheckCircle2 size={16} color="#1D4ED8" />
          <span style={{ fontSize: "13px", fontWeight: 600, color: "#1D4ED8" }}>Payment Link Created</span>
          <MockBadge isMock={result.is_mock} />
        </div>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px", marginBottom: "16px" }}>
        <InfoCell label="Amount" value={fmt(result.amount_minor)} />
        <InfoCell label="Status" value={result.status} />
        <InfoCell label="Expiry" value={fmtShort(result.expiry_at)} />
        <InfoCell label="Link ID" value={result.gateway_order_id?.slice(0, 14) + "…"} />
      </div>
      {result.short_url && (
        <div style={{ background: "#F8FAFC", border: "1px solid #E2E8F0", borderRadius: "8px", padding: "12px", marginBottom: "12px" }}>
          <p style={{ fontSize: "11px", color: "#64748B", margin: "0 0 4px" }}>Payment URL (MOCK)</p>
          <p style={{ fontSize: "12px", color: "#0F172A", wordBreak: "break-all", margin: 0 }}>{result.short_url}</p>
        </div>
      )}
      <button onClick={copyURL} style={{ width: "100%", padding: "8px", background: "#EFF6FF", border: "1px solid #BFDBFE",
        borderRadius: "8px", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: "6px",
        fontSize: "13px", color: "#1D4ED8" }}>
        <Copy size={14} />{copied ? "Copied!" : "Copy Payment URL"}
      </button>
    </div>
  );
}

function InfoCell({ label, value }) {
  return (
    <div style={{ background: "#F8FAFC", borderRadius: "6px", padding: "8px 10px" }}>
      <div style={{ fontSize: "10px", color: "#94A3B8", fontWeight: 600, textTransform: "uppercase", marginBottom: "2px" }}>{label}</div>
      <div style={{ fontSize: "13px", color: "#0F172A", fontWeight: 500 }}>{value}</div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// HISTORY TAB
// ═══════════════════════════════════════════════════════════════════════════════
function HistoryTab() {
  const [orders, setOrders] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [skip, setSkip] = useState(0);
  const [expanded, setExpanded] = useState(null);
  const limit = 20;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await gatewayApi.listOrders({ status: statusFilter || undefined, order_type: typeFilter || undefined, skip, limit });
      setOrders(r.data.items || []);
      setTotal(r.data.total || 0);
    } catch (e) {
      toast.error(formatError(e));
    } finally {
      setLoading(false);
    }
  }, [statusFilter, typeFilter, skip]);

  useEffect(() => { load(); }, [load]);

  const handleExpire = async (orderId) => {
    try {
      await gatewayApi.expireOrder(orderId);
      toast.success("Order cancelled");
      load();
    } catch (e) { toast.error(formatError(e)); }
  };

  return (
    <div style={{ background: "#fff", border: "1px solid #E2E8F0", borderRadius: "12px", padding: "20px" }}>
      {/* Filters */}
      <div style={{ display: "flex", gap: "12px", marginBottom: "16px" }}>
        <select value={statusFilter} onChange={e => { setStatusFilter(e.target.value); setSkip(0); }}
          style={{ padding: "7px 10px", border: "1px solid #E2E8F0", borderRadius: "8px", fontSize: "13px", color: "#374151" }}>
          <option value="">All Statuses</option>
          {Object.entries(ORDER_STATUS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
        </select>
        <select value={typeFilter} onChange={e => { setTypeFilter(e.target.value); setSkip(0); }}
          style={{ padding: "7px 10px", border: "1px solid #E2E8F0", borderRadius: "8px", fontSize: "13px", color: "#374151" }}>
          <option value="">All Types</option>
          <option value="QR_CODE">QR Code</option>
          <option value="PAYMENT_LINK">Payment Link</option>
        </select>
        <button onClick={load} style={{ marginLeft: "auto", background: "none", border: "none", cursor: "pointer", color: "#64748B" }}>
          <RefreshCw size={16} className={loading ? "animate-spin" : ""} />
        </button>
        <span style={{ fontSize: "12px", color: "#94A3B8", alignSelf: "center" }}>{total} orders</span>
      </div>

      {orders.length === 0 ? (
        <div style={{ textAlign: "center", padding: "48px", color: "#94A3B8" }}>
          <Clock size={40} style={{ margin: "0 auto 12px", opacity: 0.4 }} />
          <p style={{ fontSize: "14px" }}>No orders match the current filter.</p>
        </div>
      ) : (
        <>
          {orders.map(o => {
            const TIcon = TYPE_ICONS[o.order_type] || Wallet;
            const isExpanded = expanded === o.id;
            return (
              <div key={o.id} data-testid={`order-row-${o.id}`}
                style={{ borderBottom: "1px solid #F1F5F9", paddingBottom: "1px" }}>
                <div style={{ display: "flex", alignItems: "center", gap: "12px", padding: "12px 0", cursor: "pointer" }}
                  onClick={() => setExpanded(isExpanded ? null : o.id)}>
                  <TIcon size={16} color="#64748B" style={{ flexShrink: 0 }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
                      <span style={{ fontSize: "13px", fontWeight: 600, color: "#0F172A" }}>{fmt(o.amount_minor)}</span>
                      <StatusBadge status={o.status} />
                      <MockBadge isMock={o.is_mock} />
                    </div>
                    <p style={{ fontSize: "12px", color: "#64748B", margin: "2px 0 0", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {o.description || o.order_type} · {fmtDate(o.created_at)}
                    </p>
                  </div>
                  <div style={{ flexShrink: 0, color: "#94A3B8" }}>
                    {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                  </div>
                </div>

                {isExpanded && (
                  <div style={{ background: "#F8FAFC", borderRadius: "8px", padding: "16px", marginBottom: "12px" }}>
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "8px", marginBottom: "12px" }}>
                      <InfoCell label="Order ID" value={o.id?.slice(0, 18) + "…"} />
                      <InfoCell label="Gateway ID" value={o.gateway_order_id?.slice(0, 18) + "…"} />
                      <InfoCell label="Provider" value={o.gateway_provider} />
                      <InfoCell label="Type" value={o.order_type} />
                      <InfoCell label="Expiry" value={fmtShort(o.expiry_at)} />
                      <InfoCell label="Receipt" value={o.receipt_id ? o.receipt_id.slice(0, 14) + "…" : "Pending"} />
                    </div>
                    {o.qr_raw_string && (
                      <div style={{ background: "#EFF6FF", borderRadius: "6px", padding: "8px", marginBottom: "8px", fontSize: "11px", color: "#1D4ED8", wordBreak: "break-all" }}>
                        UPI URI: {o.qr_raw_string}
                      </div>
                    )}
                    {o.short_url && (
                      <div style={{ background: "#F0FDF4", borderRadius: "6px", padding: "8px", marginBottom: "8px", fontSize: "11px", color: "#166534", wordBreak: "break-all" }}>
                        URL: {o.short_url}
                      </div>
                    )}
                    {["CREATED", "ACTIVE"].includes(o.status) && (
                      <button onClick={() => handleExpire(o.id)}
                        style={{ padding: "6px 12px", background: "#FEF2F2", border: "1px solid #FECACA", color: "#DC2626",
                          borderRadius: "6px", cursor: "pointer", fontSize: "12px", fontWeight: 500 }}>
                        <Ban size={12} style={{ marginRight: "4px", verticalAlign: "middle" }} />
                        Cancel Order
                      </button>
                    )}
                  </div>
                )}
              </div>
            );
          })}

          {/* Pagination */}
          <div style={{ display: "flex", justifyContent: "center", gap: "8px", marginTop: "16px" }}>
            <button disabled={skip === 0} onClick={() => setSkip(Math.max(0, skip - limit))}
              style={{ padding: "6px 12px", border: "1px solid #E2E8F0", borderRadius: "6px", cursor: skip === 0 ? "not-allowed" : "pointer", fontSize: "12px", color: skip === 0 ? "#94A3B8" : "#374151", background: "#fff" }}>
              Previous
            </button>
            <span style={{ fontSize: "12px", color: "#64748B", alignSelf: "center" }}>{skip + 1}–{Math.min(skip + limit, total)} of {total}</span>
            <button disabled={skip + limit >= total} onClick={() => setSkip(skip + limit)}
              style={{ padding: "6px 12px", border: "1px solid #E2E8F0", borderRadius: "6px", cursor: skip + limit >= total ? "not-allowed" : "pointer", fontSize: "12px", color: skip + limit >= total ? "#94A3B8" : "#374151", background: "#fff" }}>
              Next
            </button>
          </div>
        </>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// SETTLEMENTS TAB
// ═══════════════════════════════════════════════════════════════════════════════
function SettlementsTab() {
  const [settlements, setSettlements] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [importForm, setImportForm] = useState({ settlement_id: "", settlement_date: "", gross_amount_minor: "", fee_minor: "", tax_minor: "", net_amount_minor: "", utr_number: "", notes: "" });
  const [importing, setImporting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await gatewayApi.listSettlements();
      setSettlements(r.data.items || []);
    } catch (e) { toast.error(formatError(e)); } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleImport = async (e) => {
    e.preventDefault();
    setImporting(true);
    try {
      const payload = {
        ...importForm,
        gross_amount_minor: Number(importForm.gross_amount_minor),
        fee_minor: Number(importForm.fee_minor),
        tax_minor: Number(importForm.tax_minor),
        net_amount_minor: Number(importForm.net_amount_minor),
      };
      await gatewayApi.importSettlement(payload);
      toast.success("Settlement imported");
      setShowImport(false);
      setImportForm({ settlement_id: "", settlement_date: "", gross_amount_minor: "", fee_minor: "", tax_minor: "", net_amount_minor: "", utr_number: "", notes: "" });
      load();
    } catch (e) { toast.error(formatError(e)); } finally { setImporting(false); }
  };

  const RECON_STATUS = { PENDING: { color: "#D97706", bg: "#FFF3CD" }, MATCHED: { color: "#166534", bg: "#F0FDF4" }, EXCEPTION: { color: "#DC2626", bg: "#FEF2F2" } };

  return (
    <div>
      <div style={{ background: "#fff", border: "1px solid #E2E8F0", borderRadius: "12px", padding: "20px", marginBottom: "16px" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }}>
          <h3 style={{ fontSize: "15px", fontWeight: 600, color: "#0F172A", margin: 0 }}>Settlement Records</h3>
          <div style={{ display: "flex", gap: "8px" }}>
            <button onClick={load} style={{ background: "none", border: "none", cursor: "pointer", color: "#64748B" }}>
              <RefreshCw size={16} className={loading ? "animate-spin" : ""} />
            </button>
            <button onClick={() => setShowImport(v => !v)} data-testid="import-settlement-btn"
              style={{ padding: "7px 14px", background: "#2563EB", color: "#fff", border: "none", borderRadius: "8px", cursor: "pointer", fontSize: "13px", fontWeight: 600 }}>
              <Download size={14} style={{ verticalAlign: "middle", marginRight: "4px" }} />
              Import Settlement
            </button>
          </div>
        </div>

        {showImport && (
          <div style={{ background: "#F8FAFC", border: "1px solid #E2E8F0", borderRadius: "10px", padding: "16px", marginBottom: "16px" }}>
            <h4 style={{ fontSize: "14px", fontWeight: 600, color: "#374151", margin: "0 0 12px" }}>Import Settlement Batch</h4>
            <form onSubmit={handleImport}>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px" }}>
                {[
                  { key: "settlement_id", label: "Settlement ID *", type: "text" },
                  { key: "settlement_date", label: "Settlement Date *", type: "date" },
                  { key: "gross_amount_minor", label: "Gross (paise) *", type: "number" },
                  { key: "fee_minor", label: "Fee (paise) *", type: "number" },
                  { key: "tax_minor", label: "Tax on Fee (paise) *", type: "number" },
                  { key: "net_amount_minor", label: "Net (paise) *", type: "number" },
                  { key: "utr_number", label: "UTR Number", type: "text" },
                  { key: "notes", label: "Notes", type: "text" },
                ].map(({ key, label, type }) => (
                  <div key={key}>
                    <label style={{ fontSize: "11px", fontWeight: 600, color: "#374151", display: "block", marginBottom: "4px" }}>{label}</label>
                    <input type={type} value={importForm[key]} required={label.includes("*")}
                      onChange={e => setImportForm(f => ({ ...f, [key]: e.target.value }))}
                      style={{ width: "100%", padding: "7px 10px", border: "1px solid #E2E8F0", borderRadius: "7px", fontSize: "13px", boxSizing: "border-box" }} />
                  </div>
                ))}
              </div>
              <div style={{ display: "flex", gap: "8px", marginTop: "12px" }}>
                <button type="submit" disabled={importing}
                  style={{ padding: "8px 16px", background: importing ? "#94A3B8" : "#166534", color: "#fff", border: "none", borderRadius: "8px", cursor: importing ? "not-allowed" : "pointer", fontSize: "13px", fontWeight: 600 }}>
                  {importing ? "Importing…" : "Import"}
                </button>
                <button type="button" onClick={() => setShowImport(false)}
                  style={{ padding: "8px 16px", background: "#F1F5F9", color: "#374151", border: "1px solid #E2E8F0", borderRadius: "8px", cursor: "pointer", fontSize: "13px" }}>
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        {settlements.length === 0 ? (
          <div style={{ textAlign: "center", padding: "40px", color: "#94A3B8" }}>
            <ArrowDownLeft size={40} style={{ margin: "0 auto 12px", opacity: 0.4 }} />
            <p style={{ fontSize: "14px" }}>No settlements imported. Use Import Settlement to add a Razorpay settlement record.</p>
          </div>
        ) : (
          <table style={{ width: "100%", borderCollapse: "collapse" }} data-testid="settlements-table">
            <thead>
              <tr style={{ borderBottom: "1px solid #F1F5F9" }}>
                {["Settlement ID", "Date", "Gross", "Fee+Tax", "Net", "UTR", "Status"].map(h => (
                  <th key={h} style={{ textAlign: "left", padding: "8px 10px", fontSize: "12px", color: "#64748B", fontWeight: 600 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {settlements.map(s => {
                const sc = RECON_STATUS[s.reconciliation_status] || { color: "#64748B", bg: "#F1F5F9" };
                return (
                  <tr key={s.id} style={{ borderBottom: "1px solid #F8FAFC" }}>
                    <td style={{ padding: "10px", fontSize: "12px", fontFamily: "monospace", color: "#374151" }}>{s.settlement_id?.slice(0, 20)}</td>
                    <td style={{ padding: "10px", fontSize: "12px", color: "#374151" }}>{fmtShort(s.settlement_date)}</td>
                    <td style={{ padding: "10px", fontSize: "13px", fontWeight: 600, color: "#0F172A" }}>{fmt(s.gross_amount_minor)}</td>
                    <td style={{ padding: "10px", fontSize: "12px", color: "#DC2626" }}>{fmt((s.fee_minor || 0) + (s.tax_minor || 0))}</td>
                    <td style={{ padding: "10px", fontSize: "13px", fontWeight: 600, color: "#166534" }}>{fmt(s.net_amount_minor)}</td>
                    <td style={{ padding: "10px", fontSize: "11px", color: "#64748B" }}>{s.utr_number || "—"}</td>
                    <td style={{ padding: "10px" }}>
                      <span style={{ background: sc.bg, color: sc.color, fontSize: "11px", fontWeight: 600, padding: "2px 8px", borderRadius: "9999px", border: `1px solid ${sc.color}30` }}>
                        {s.reconciliation_status}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// CONFIG TAB
// ═══════════════════════════════════════════════════════════════════════════════
function ConfigTab() {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    gatewayApi.getConfig().then(r => setConfig(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  if (loading) return <div style={{ textAlign: "center", padding: "40px", color: "#94A3B8" }}>Loading config…</div>;
  if (!config) return <div style={{ textAlign: "center", padding: "40px", color: "#DC2626" }}>Gateway configuration not found. Run PAY-01D migration.</div>;

  const fields = [
    ["Provider", config.provider],
    ["Mode", config.mode],
    ["Activation Status", config.activation_status],
    ["Legal Entity", config.legal_entity_id],
    ["Is Active", config.is_active ? "Yes" : "No"],
    ["Key ID Ref", config.key_id_secret_ref || "—"],
    ["Key Secret Ref", config.key_secret_ref || "—"],
    ["Webhook Secret Ref", config.webhook_secret_ref || "—"],
    ["Fee Account Code", config.gateway_fee_account_code || "—"],
    ["Settlement Bank", config.settlement_bank_account_id || "Not configured"],
    ["Notes", config.notes],
  ];

  return (
    <div>
      <div style={{ background: "#FFFBEB", border: "1px solid #FCD34D", borderRadius: "10px", padding: "14px", marginBottom: "20px" }}>
        <div style={{ display: "flex", gap: "8px", alignItems: "flex-start" }}>
          <AlertTriangle size={16} color="#D97706" style={{ flexShrink: 0, marginTop: "1px" }} />
          <div>
            <p style={{ margin: 0, fontSize: "13px", fontWeight: 600, color: "#92400E" }}>
              Production Activation: NOT AUTHORIZED
            </p>
            <p style={{ margin: "4px 0 0", fontSize: "12px", color: "#92400E" }}>
              Gateway is in MOCK mode. Razorpay sandbox credentials are required to enable SANDBOX_ACTIVE status.
              Live activation requires Finance Owner authorization + credential provisioning.
            </p>
          </div>
        </div>
      </div>

      <div data-testid="gateway-config-panel" style={{ background: "#fff", border: "1px solid #E2E8F0", borderRadius: "12px", padding: "20px" }}>
        <h3 style={{ fontSize: "15px", fontWeight: 600, color: "#0F172A", margin: "0 0 16px" }}>Gateway Configuration (Read-Only)</h3>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px" }}>
          {fields.map(([label, value]) => (
            <div key={label} style={{ background: "#F8FAFC", borderRadius: "8px", padding: "10px 12px" }}>
              <div style={{ fontSize: "11px", color: "#94A3B8", fontWeight: 600, textTransform: "uppercase", marginBottom: "3px" }}>{label}</div>
              <div style={{ fontSize: "13px", color: "#0F172A", fontWeight: label === "Activation Status" ? 700 : 500 }}>
                {label === "Activation Status" ? (
                  <span style={{ color: config.activation_status === "LIVE_ACTIVATED" ? "#166534" : config.activation_status === "UNCONFIGURED" ? "#92400E" : "#1D4ED8" }}>
                    {value}
                  </span>
                ) : value}
              </div>
            </div>
          ))}
        </div>

        {/* Credential reference explanation */}
        <div style={{ marginTop: "20px", background: "#F0FDF4", border: "1px solid #BBF7D0", borderRadius: "8px", padding: "14px" }}>
          <div style={{ display: "flex", gap: "8px", alignItems: "flex-start" }}>
            <Shield size={16} color="#166534" style={{ flexShrink: 0 }} />
            <div>
              <p style={{ margin: "0 0 6px", fontSize: "13px", fontWeight: 600, color: "#166534" }}>
                Credential Security (SecretResolver)
              </p>
              <p style={{ margin: 0, fontSize: "12px", color: "#166534" }}>
                Credentials are referenced via <code>env://RAZORPAY_*</code> references — never stored in MongoDB.
                PAY-01D-02 is BLOCKED_BY_CREDENTIALS until Razorpay test keys are provisioned as environment variables.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// MAIN PAGE
// ═══════════════════════════════════════════════════════════════════════════════
export default function GatewayPaymentsWorkspace() {
  const { user } = useAuth();
  const [tab, setTab] = useState("overview");
  const [summary, setSummary] = useState(null);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);

  const hasView   = (user?.capabilities || []).includes("payment.gateway.view");
  const hasManage = (user?.capabilities || []).includes("payment.gateway.manage");

  const loadOverview = useCallback(async () => {
    setLoading(true);
    try {
      const [sumR, ordR] = await Promise.all([
        gatewayApi.getSummary(),
        gatewayApi.listOrders({ limit: 10 }),
      ]);
      setSummary(sumR.data);
      setOrders(ordR.data.items || []);
    } catch (e) {
      toast.error(formatError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadOverview(); }, [loadOverview]);

  if (!hasView) {
    return (
      <div style={{ padding: "32px", textAlign: "center" }}>
        <Shield size={40} color="#94A3B8" style={{ margin: "0 auto 12px" }} />
        <p style={{ color: "#64748B" }}>You do not have permission to view gateway payments.</p>
        <p style={{ fontSize: "12px", color: "#94A3B8" }}>Required: <code>payment.gateway.view</code></p>
      </div>
    );
  }

  return (
    <div style={{ padding: "24px", fontFamily: "'Inter', sans-serif", maxWidth: "1400px", margin: "0 auto" }}>
      {/* Header */}
      <div style={{ marginBottom: "24px" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "4px" }}>
              <QrCode size={22} color="#2563EB" />
              <h1 style={{ margin: 0, fontSize: "22px", fontWeight: 700, color: "#0F172A" }}>
                Gateway Payments
              </h1>
              <span style={{ background: "#FFFBEB", color: "#92400E", fontSize: "11px", fontWeight: 700,
                padding: "2px 8px", borderRadius: "9999px", border: "1px solid #FCD34D" }}>
                MOCK UAT MODE
              </span>
            </div>
            <p style={{ margin: 0, fontSize: "13px", color: "#64748B" }}>
              PAY-01D — Razorpay gateway orders, QR payments, settlement reconciliation
            </p>
          </div>
          <button onClick={loadOverview} disabled={loading}
            style={{ padding: "8px 16px", background: "#F8FAFC", border: "1px solid #E2E8F0",
              borderRadius: "8px", cursor: "pointer", display: "flex", alignItems: "center", gap: "6px",
              fontSize: "13px", color: "#374151" }}>
            <RefreshCw size={14} className={loading ? "animate-spin" : ""} /> Refresh
          </button>
        </div>
      </div>

      {/* No-manage warning */}
      {!hasManage && (
        <div style={{ background: "#F8FAFC", border: "1px solid #E2E8F0", borderRadius: "8px", padding: "10px 14px", marginBottom: "16px",
          display: "flex", alignItems: "center", gap: "8px" }}>
          <Eye size={14} color="#64748B" />
          <span style={{ fontSize: "12px", color: "#64748B" }}>View-only mode — <code>payment.gateway.manage</code> required to create payment requests.</span>
        </div>
      )}

      {/* Tabs */}
      <div style={{ display: "flex", gap: "4px", borderBottom: "2px solid #E2E8F0", marginBottom: "24px" }}>
        {TABS.map(({ id, label, icon: Icon }) => (
          <button key={id} onClick={() => setTab(id)} data-testid={`tab-${id}`}
            style={{ display: "flex", alignItems: "center", gap: "6px", padding: "10px 16px",
              background: "none", border: "none", cursor: "pointer", fontSize: "13px", fontWeight: 600,
              color: tab === id ? "#2563EB" : "#64748B",
              borderBottom: tab === id ? "2px solid #2563EB" : "2px solid transparent",
              marginBottom: "-2px", transition: "all 0.15s" }}>
            <Icon size={14} />{label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      {tab === "overview"    && <OverviewTab summary={summary} orders={orders} loading={loading} onRefresh={loadOverview} />}
      {tab === "initiate"    && <InitiateTab onCreated={loadOverview} />}
      {tab === "history"     && <HistoryTab />}
      {tab === "settlements" && <SettlementsTab />}
      {tab === "config"      && <ConfigTab />}
    </div>
  );
}
